import React, { memo, useState, useEffect, useRef } from 'react';

interface VirtualizedListProps<T> {
    items: T[];
    renderItem: (item: T, index: number) => React.ReactNode;
}

/**
 * Component for virtualized rendering of large lists
 * @param props - Component props
 * @returns Rendered component
 */
const VirtualizedList = memo(
    <T,>({ items, renderItem }: VirtualizedListProps<T>) => {
        const [visibleItems, setVisibleItems] = useState<T[]>([]);
        const containerRef = useRef<HTMLDivElement>(null);

        // Only render a subset of items if there are too many
        useEffect(() => {
            if (items.length > 100) {
                setVisibleItems(items.slice(0, 50));
            } else {
                setVisibleItems(items);
            }
        }, [items]);

        return (
            <div ref={containerRef} className="virtual-container">
                {visibleItems.map((item, index) => renderItem(item, index))}
                {items.length > 100 && (
                    <div className="text-gray-500 italic">
                        Showing 50 of {items.length} items to improve
                        performance
                    </div>
                )}
            </div>
        );
    },
);

// Make sure we're exporting the component correctly
export default VirtualizedList;
